(() => {
  const root = document.documentElement;
  let viewportFrame = 0;

  const prefersReducedMotion = () => (
    root.dataset.fvMotion === 'reduce'
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const updateViewportHeight = () => {
    viewportFrame = 0;
    const height = window.visualViewport?.height || window.innerHeight;
    if (height > 0) root.style.setProperty('--fv-menu-viewport', `${Math.round(height)}px`);
  };

  const queueViewportHeight = () => {
    if (viewportFrame) return;
    viewportFrame = window.requestAnimationFrame(updateViewportHeight);
  };

  const finishMenuClose = (menu, restoreFocus = true) => {
    if (!menu) return;
    menu.classList.remove('is-closing', 'is-entering');
    menu.removeAttribute('open');
    menu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    if (!document.querySelector('[data-fv2-menu][open]')) document.body.classList.remove('fv2-lock');
    if (restoreFocus) menu.querySelector('summary')?.focus({ preventScroll: true });
  };

  const closeMenu = (menu, restoreFocus = true) => {
    if (!menu?.open || menu.classList.contains('is-closing')) return;
    if (prefersReducedMotion()) {
      finishMenuClose(menu, restoreFocus);
      return;
    }

    menu.classList.remove('is-entering');
    menu.classList.add('is-closing');
    const drawer = menu.querySelector('.fv2-menu__drawer');
    let closed = false;
    const complete = () => {
      if (closed) return;
      closed = true;
      finishMenuClose(menu, restoreFocus);
    };
    drawer?.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform') complete();
    }, { once: true });
    window.setTimeout(complete, 320);
  };

  const initMenu = (menu) => {
    if (menu.dataset.fv266Ready) return;
    menu.dataset.fv266Ready = 'true';
    const summary = menu.querySelector('summary');
    summary?.setAttribute('aria-expanded', String(menu.open));

    menu.addEventListener('toggle', () => {
      summary?.setAttribute('aria-expanded', String(menu.open));
      if (!menu.open || menu.classList.contains('is-closing') || prefersReducedMotion()) return;
      menu.classList.add('is-entering');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => menu.classList.remove('is-entering'));
      });
    });
  };

  const init = () => {
    try {
      localStorage.removeItem('furavoraThemeV1');
    } catch (_error) {}
    delete root.dataset.fvTheme;
    delete root.dataset.fvThemePreference;
    updateViewportHeight();
    document.querySelectorAll('[data-fv2-menu]').forEach(initMenu);
  };

  /* Capture close actions before the legacy instant-close listeners. */
  document.addEventListener('click', (event) => {
    const closeControl = event.target.closest?.('[data-fv2-menu-close]');
    if (!closeControl) return;
    const menu = closeControl.closest('[data-fv2-menu]');
    if (!menu?.open) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeMenu(menu, true);
  }, true);

  /* Let same-site menu destinations leave after the close motion completes. */
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('[data-fv2-menu] .fv2-menu__drawer a[href]');
    const menu = link?.closest('[data-fv2-menu]');
    if (!link || !menu?.open || link.target === '_blank' || link.hasAttribute('download')) return;

    let destination;
    try {
      destination = new URL(link.href, window.location.href);
    } catch (_error) {
      return;
    }
    if (!['http:', 'https:'].includes(destination.protocol) || destination.origin !== window.location.origin) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const delay = prefersReducedMotion() ? 0 : 260;
    closeMenu(menu, false);
    window.setTimeout(() => window.location.assign(destination.href), delay);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = document.querySelector('[data-fv2-menu][open]');
    if (!menu) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeMenu(menu, true);
  }, true);

  window.addEventListener('resize', queueViewportHeight, { passive: true });
  window.addEventListener('orientationchange', queueViewportHeight, { passive: true });
  window.visualViewport?.addEventListener('resize', queueViewportHeight, { passive: true });
  window.visualViewport?.addEventListener('scroll', queueViewportHeight, { passive: true });

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
